import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock, Bell, Star, ShoppingCart, RotateCcw, Save, Plus, Trash2,
  CheckCircle, AlertTriangle, MessageSquare, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

const EVENT_META = {
  unpaid_reminder: { icon: Clock, label: 'Reminder Belum Bayar', color: 'text-red-500', bg: 'bg-red-50', desc: 'Kirim reminder otomatis untuk pesanan yang belum dibayar' },
  post_delivery_review: { icon: Star, label: 'Follow-Up Review', color: 'text-amber-500', bg: 'bg-amber-50', desc: 'Minta review setelah pesanan diterima customer' },
  repeat_order: { icon: RotateCcw, label: 'Repeat Order', color: 'text-blue-500', bg: 'bg-blue-50', desc: 'Follow-up customer untuk beli lagi setelah X hari' },
  cart_abandoned: { icon: ShoppingCart, label: 'Keranjang Ditinggalkan', color: 'text-purple-500', bg: 'bg-purple-50', desc: 'Ingatkan customer yang meninggalkan keranjang belanja' },
};

const VARIABLES = [
  '{customer_name}', '{order_number}', '{total}', '{product_name}', '{payment_url}'
];

function formatDelay(hours) {
  if (hours < 24) return `${hours} jam`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem > 0 ? `${days} hari ${rem} jam` : `${days} hari`;
}

export default function FollowUpSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expanded, setExpanded] = useState({});
  const [hasGateway, setHasGateway] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/followup-rules');
      setRules(data.rules || []);
      // Check gateway status
      const { data: qd } = await api.get('/tenant/followup-queue', { params: { limit: 1 } });
      setHasGateway(qd.has_wa_gateway);
    } catch (err) {
      console.error('Failed to load rules', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const toggleActive = async (rule) => {
    try {
      await api.put(`/tenant/followup-rules/${rule.id}`, { is_active: !rule.is_active });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch (err) {
      console.error('Toggle error', err);
    }
  };

  const updateStage = (ruleId, stageIdx, field, value) => {
    setRules(prev => prev.map(r => {
      if (r.id !== ruleId) return r;
      const stages = [...r.stages];
      stages[stageIdx] = { ...stages[stageIdx], [field]: value };
      return { ...r, stages };
    }));
  };

  const addStage = (ruleId) => {
    setRules(prev => prev.map(r => {
      if (r.id !== ruleId) return r;
      const stages = [...r.stages];
      const lastDelay = stages.length > 0 ? stages[stages.length - 1].delay_hours : 0;
      stages.push({ stage_number: stages.length + 1, delay_hours: lastDelay + 24, template_message: '' });
      return { ...r, stages };
    }));
  };

  const removeStage = (ruleId, stageIdx) => {
    setRules(prev => prev.map(r => {
      if (r.id !== ruleId) return r;
      const stages = r.stages.filter((_, i) => i !== stageIdx).map((s, i) => ({ ...s, stage_number: i + 1 }));
      return { ...r, stages };
    }));
  };

  const saveRule = async (rule) => {
    setSaving(prev => ({ ...prev, [rule.id]: true }));
    try {
      await api.put(`/tenant/followup-rules/${rule.id}`, { stages: rule.stages });
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(prev => ({ ...prev, [rule.id]: false }));
    }
  };

  const insertVariable = (ruleId, stageIdx, variable) => {
    setRules(prev => prev.map(r => {
      if (r.id !== ruleId) return r;
      const stages = [...r.stages];
      stages[stageIdx] = { ...stages[stageIdx], template_message: stages[stageIdx].template_message + variable };
      return { ...r, stages };
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Automasi Follow-Up</h1>
        <p className="text-sm text-slate-500 mt-1">Atur jadwal & template pesan follow-up otomatis ke customer via WhatsApp</p>
      </div>

      {/* WA Gateway Status */}
      {hasGateway ? (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <CheckCircle size={20} className="text-emerald-500" />
          <div>
            <p className="text-sm font-medium text-emerald-800">WA Gateway Aktif (Responic)</p>
            <p className="text-xs text-emerald-600">Follow-up akan terkirim otomatis via WhatsApp API</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertTriangle size={20} className="text-amber-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">Mode Manual — WA Gateway Belum Aktif</p>
            <p className="text-xs text-amber-600">Follow-up akan muncul di antrian dengan tombol kirim manual via wa.me link</p>
          </div>
          <a href="/dashboard/settings" className="text-xs text-amber-700 underline hover:text-amber-900">Aktifkan Responic →</a>
        </div>
      )}

      {/* Rules */}
      {rules.map(rule => {
        const meta = EVENT_META[rule.event] || { icon: Bell, label: rule.event, color: 'text-slate-500', bg: 'bg-slate-50', desc: '' };
        const Icon = meta.icon;
        const isExpanded = expanded[rule.id];

        return (
          <Card key={rule.id} className={`border-slate-200 shadow-sm ${!rule.is_active ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(prev => ({ ...prev, [rule.id]: !prev[rule.id] }))}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center`}>
                    <Icon size={20} className={meta.color} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{meta.label}</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{rule.stages?.length || 0} stage</span>
                  <button
                    onClick={e => { e.stopPropagation(); toggleActive(rule); }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${rule.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${rule.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="space-y-4 pt-2">
                {rule.stages?.map((stage, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-white bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center">{stage.stage_number}</span>
                        <span className="text-sm font-medium text-slate-700">Stage {stage.stage_number}</span>
                      </div>
                      {rule.stages.length > 1 && (
                        <button onClick={() => removeStage(rule.id, idx)} className="text-red-400 hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    {/* Delay */}
                    <div className="flex items-center gap-3">
                      <label className="text-xs text-slate-500 w-20 shrink-0">Kirim setelah</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={stage.delay_hours}
                          onChange={e => updateStage(rule.id, idx, 'delay_hours', parseInt(e.target.value) || 1)}
                          className="w-20 px-2 py-1.5 border border-slate-300 rounded-md text-sm text-center"
                        />
                        <span className="text-xs text-slate-500">jam</span>
                        <span className="text-xs text-slate-400">({formatDelay(stage.delay_hours)})</span>
                      </div>
                    </div>

                    {/* Template */}
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Template Pesan</label>
                      <textarea
                        value={stage.template_message}
                        onChange={e => updateStage(rule.id, idx, 'template_message', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Tulis pesan follow-up..."
                      />
                      <div className="flex flex-wrap gap-1 mt-1">
                        {VARIABLES.map(v => (
                          <button
                            key={v}
                            onClick={() => insertVariable(rule.id, idx, v)}
                            className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors"
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-2">
                  <Button variant="outline" size="sm" onClick={() => addStage(rule.id)}>
                    <Plus size={14} className="mr-1" /> Tambah Stage
                  </Button>
                  <Button size="sm" onClick={() => saveRule(rule)} disabled={saving[rule.id]}>
                    {saving[rule.id] ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                    Simpan
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {rules.length === 0 && (
        <div className="text-center text-slate-400 py-12">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Belum ada rule follow-up. Refresh halaman untuk generate default.</p>
        </div>
      )}
    </div>
  );
}
